/**
 * Error Logging Service
 * 
 * Logs frontend errors to the backend API, which then logs them to Application Insights.
 * This approach is free and uses existing infrastructure.
 * 
 * Includes protections against:
 * - Infinite loops (errors in logging don't trigger more logs)
 * - Spam/abuse (client-side throttling and deduplication)
 */

interface ErrorLog {
    message: string;
    stack?: string;
    componentStack?: string;
    url: string;
    userAgent: string;
    timestamp: string;
    userId?: string;
    teamId?: string;
    sessionId?: string;
    environment: string;
}

// Client-side throttling and deduplication
const errorCache = new Map<string, number>();
const MAX_ERRORS_PER_MINUTE = 10; // Client-side limit
const DEDUPE_WINDOW_MS = 5 * 60 * 1000; // 5 minutes
let isLogging = false; // Prevent recursive logging

/**
 * Generate a simple hash for error deduplication
 */
function getErrorHash(message: string, stack?: string, url?: string): string {
    const hashInput = `${message}|${stack || ''}|${url || ''}`;
    // Simple hash function (not cryptographically secure, but good enough for deduplication)
    let hash = 0;
    for (let i = 0; i < hashInput.length; i++) {
        const char = hashInput.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32-bit integer
    }
    return hash.toString(36);
}

/**
 * Check if we should throttle this error (client-side rate limiting)
 */
function shouldThrottleError(errorHash: string): boolean {
    const now = Date.now();

    // Clean up old entries
    for (const [hash, timestamp] of errorCache.entries()) {
        if (now - timestamp > DEDUPE_WINDOW_MS) {
            errorCache.delete(hash);
        }
    }

    // Check if this is a duplicate (same error within dedupe window)
    if (errorCache.has(errorHash)) {
        return true; // Throttle - duplicate error
    }

    // Count errors in the last minute
    const errorsInLastMinute = Array.from(errorCache.values())
        .filter(timestamp => now - timestamp < 60000)
        .length;

    if (errorsInLastMinute >= MAX_ERRORS_PER_MINUTE) {
        return true; // Throttle - too many errors
    }

    // Add to cache
    errorCache.set(errorHash, now);
    return false; // Don't throttle
}

/**
 * Decode JWT token to extract claims (without verification)
 * This is safe for client-side use as we only extract public claims
 */
function decodeJwtToken(token: string): any | null {
    try {
        const parts = token.split('.');
        if (parts.length !== 3) {
            return null;
        }
        const payload = parts[1];
        // Add padding if needed for base64 decoding
        const paddedPayload = payload + '='.repeat((4 - payload.length % 4) % 4);
        const decoded = atob(paddedPayload.replace(/-/g, '+').replace(/_/g, '/'));
        return JSON.parse(decoded);
    } catch {
        return null;
    }
}

/**
 * Get current user info and session ID from sessionStorage
 * SessionId is populated from the /api/v1/auth/me endpoint which extracts it from the JWT token
 * This is a utility function to get user info without React hooks
 */
function getCurrentUserInfo(): { userId?: string; teamId?: string; sessionId?: string } {
    try {
        const authState = sessionStorage.getItem('auth_state');
        if (authState) {
            const parsed = JSON.parse(authState);
            if (parsed?.user) {
                return {
                    userId: parsed.user.userId,
                    teamId: parsed.user.teamId,
                    sessionId: parsed.user.sessionId, // Extracted from JWT token by /api/v1/auth/me endpoint
                };
            }
        }
    } catch {
        // Silently fail - can't access user info
    }
    return {};
}

/**
 * Log an error to the backend API
 * Includes protections against infinite loops and spam
 */
export async function logError(
    error: Error,
    errorInfo?: { componentStack?: string },
    userId?: string,
    teamId?: string
): Promise<void> {
    // CRITICAL: Prevent infinite loops - if we're already logging, don't log again
    if (isLogging) {
        return;
    }

    try {
        isLogging = true;

        const message = error.message || error.toString();
        const stack = error.stack;
        const componentStack = errorInfo?.componentStack;
        const url = window.location.href;

        // Client-side deduplication and throttling
        const errorHash = getErrorHash(message, stack, url);
        if (shouldThrottleError(errorHash)) {
            // Silently skip - don't log throttled errors
            return;
        }

        // Get user info from sessionStorage if not provided
        const userInfo = userId || teamId ? { userId, teamId } : getCurrentUserInfo();
        const finalUserId = userId || userInfo.userId;
        const finalTeamId = teamId || userInfo.teamId;
        const finalSessionId = userInfo.sessionId;

        const errorLog: ErrorLog = {
            message,
            stack,
            componentStack,
            url,
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString(),
            userId: finalUserId,
            teamId: finalTeamId,
            sessionId: finalSessionId,
            environment: import.meta.env.MODE || 'unknown',
        };

        // Send to backend API
        const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
        const response = await fetch(`${apiBaseUrl}/errors`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(errorLog),
            // Don't throw on error - we don't want error logging to cause more errors
        });

        if (!response.ok) {
            // Don't log this - could cause infinite loop
            // Just silently fail
        }
    } catch (loggingError) {
        // CRITICAL: Never log errors from error logging - this prevents infinite loops
        // Silently fail - we don't want error logging to cause more errors
    } finally {
        isLogging = false;
    }
}

/**
 * Log unhandled promise rejections
 */
export function setupGlobalErrorHandlers(): void {
    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
        const error = event.reason instanceof Error
            ? event.reason
            : new Error(String(event.reason));

        logError(error, undefined, undefined);
    });

    // Handle global errors (already caught by ErrorBoundary, but good to have as backup)
    window.addEventListener('error', (event) => {
        if (event.error) {
            logError(event.error, undefined, undefined);
        }
    });
}
